import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  IconButton,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  CloudUpload as CloudUploadIcon,
  Crop as CropIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { mediaApi, mediaUrl, Set as SetType, Media } from '../services/api';
import { getSampleSetPhotoUrl, fetchSampleSetPhotoFile, isFeaturedMedia } from '../utils/setPhoto';
import {
  getSetPhotoUploadGuidance,
  UPLOAD_PROXY_SAFE_MAX_BYTES,
} from '../utils/compressImageForUpload';
import {
  isStaticOnlyLoginHost,
  WORKING_LOGIN_ORIGIN,
  toErrorMessage,
} from '../utils/errorUtils';
import PhotoCropDialog from './PhotoCropDialog';
import { fileToDataUrl } from '../utils/fileToDataUrl';

interface SetPhotoDialogProps {
  open: boolean;
  onClose: () => void;
  selectedSet: SetType | null;
  onSetUpdated?: () => void;
}

const SetPhotoDialog: React.FC<SetPhotoDialogProps> = ({
  open,
  onClose,
  selectedSet,
  onSetUpdated,
}) => {
  const [setMedia, setSetMedia] = useState<Media[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState<string | null>(null);
  const [enhancePhoto, setEnhancePhoto] = useState(true);
  const [cropSourceFile, setCropSourceFile] = useState<File | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  /** When set, crop confirm replaces this existing media row instead of staging a new upload. */
  const [reframeMediaId, setReframeMediaId] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flashSuccess = (message: string) => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    setUploadError(null);
    setUploadSuccess(message);
    successTimerRef.current = setTimeout(() => {
      setUploadSuccess(null);
      successTimerRef.current = null;
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!selectedFile) {
      setPreviewUrl(null);
      return;
    }
    // data: URL so preview works under production CSP (blob: is blocked).
    fileToDataUrl(selectedFile)
      .then((dataUrl) => {
        if (!cancelled) setPreviewUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedFile]);

  const fetchSetMedia = async (setId: number) => {
    try {
      console.log('Fetching media for set ID:', setId);
      const response = await mediaApi.getBySetId(setId);
      console.log('Media API response:', response.data);
      
      // Filter out PDFs and only keep images for photo dialog
      const imageMedia = response.data.filter((media: any) => 
        media.file_type === 'image' || 
        media.mime_type?.startsWith('image/') ||
        (media.file_name && media.file_name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp|bmp)$/))
      );
      
      setSetMedia(imageMedia);
      console.log('setMedia state updated with filtered images:', imageMedia);
    } catch (err) {
      console.error('Error fetching set media:', err);
    }
  };

  useEffect(() => {
    if (open && selectedSet) {
      fetchSetMedia(selectedSet.set_id);
    }
  }, [open, selectedSet]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so the same file can be re-picked after cancel.
    event.target.value = '';
    if (file && (file.type.startsWith('image/') || /\.(jpe?g|png|webp|gif|bmp)$/i.test(file.name))) {
      setUploadError(null);
      setReframeMediaId(null);
      setCropSourceFile(file);
      setCropDialogOpen(true);
    } else {
      setUploadError('Please select a valid image file');
    }
  };

  const handleCropCancel = () => {
    setCropDialogOpen(false);
    setCropSourceFile(null);
    setReframeMediaId(null);
  };

  const handleCropConfirm = async (croppedFile: File) => {
    const replaceId = reframeMediaId;
    setCropDialogOpen(false);
    setCropSourceFile(null);
    setReframeMediaId(null);

    if (replaceId != null) {
      if (!selectedSet) return;
      setUploading(true);
      setUploadError(null);
      setUploadSuccess(null);
      try {
        const replaceResponse = await mediaApi.replace(replaceId, croppedFile, {
          enhance: enhancePhoto,
        });
        const enhanced = Boolean(replaceResponse.data?.processing?.enhance_applied);
        flashSuccess(
          enhanced
            ? 'Photo reframed, enhanced, and saved!'
            : 'Photo reframed and saved!'
        );
        await fetchSetMedia(selectedSet.set_id);
        if (onSetUpdated) onSetUpdated();
      } catch (err: any) {
        console.error('Error replacing photo:', err);
        setUploadError(
          `Failed to save reframed photo: ${toErrorMessage(err, 'Unknown error')}`
        );
      } finally {
        setUploading(false);
      }
      return;
    }

    setSelectedFile(croppedFile);
  };

  const handleCropSkip = (originalFile: File) => {
    setCropDialogOpen(false);
    setCropSourceFile(null);
    setReframeMediaId(null);
    setSelectedFile(originalFile);
  };

  const handleRecrop = () => {
    if (!selectedFile) return;
    setReframeMediaId(null);
    setCropSourceFile(selectedFile);
    setCropDialogOpen(true);
  };

  /**
   * Resolve a gallery image URL that the browser can fetch for reframing.
   * Prefer same-origin /api/uploads (CRA/Netlify proxy) so we avoid CORS when
   * mediaUrl points at the absolute API host (e.g. localhost:5001).
   */
  const resolveReframeFetchUrl = (raw?: string | null): string | undefined => {
    const resolved = mediaUrl(raw) || raw || undefined;
    if (!resolved) return undefined;
    try {
      if (/^https?:\/\//i.test(resolved)) {
        const u = new URL(resolved);
        if (
          u.pathname.startsWith('/api/uploads/') ||
          u.pathname.startsWith('/uploads/')
        ) {
          const path = u.pathname.startsWith('/uploads/')
            ? `/api${u.pathname}`
            : u.pathname;
          return `${path}${u.search}`;
        }
      }
    } catch {
      // fall through
    }
    return resolved;
  };

  /** Open crop/zoom/rotate editor for an already-uploaded gallery photo. */
  const handleReframePhoto = async (mediaId: number) => {
    const media = setMedia.find((m) => m.media_id === mediaId);
    if (!media) return;

    setUploading(true);
    setUploadError(null);
    try {
      const url = resolveReframeFetchUrl(media.file_url);
      if (!url) {
        throw new Error('Photo URL is missing — try re-uploading this photo.');
      }
      // Uploads are public; omit credentials so wildcard CORS (if any) still works
      // when the relative proxy path is unavailable.
      const res = await fetch(url, { credentials: 'omit', cache: 'no-store' });
      if (!res.ok) {
        throw new Error(
          `Could not load photo for editing (HTTP ${res.status}). Check that /api/uploads is reachable.`
        );
      }
      const blob = await res.blob();
      const baseName =
        (media.file_name && String(media.file_name).replace(/\.[^.]+$/, '')) ||
        'set-photo';
      const file = new File([blob], `${baseName}.jpg`, {
        type: blob.type || 'image/jpeg',
        lastModified: Date.now(),
      });
      setReframeMediaId(mediaId);
      setCropSourceFile(file);
      setCropDialogOpen(true);
    } catch (err: any) {
      console.error('Error loading photo for reframe:', err);
      setUploadError(
        `Failed to open photo editor: ${toErrorMessage(err, 'Unknown error')}`
      );
    } finally {
      setUploading(false);
    }
  };


  const handleUseSamplePhoto = async () => {
    if (!selectedSet) return;
    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    try {
      const sampleFile = await fetchSampleSetPhotoFile(selectedSet.category);
      await mediaApi.upload(
        sampleFile,
        selectedSet.set_id,
        undefined,
        'photo',
        `Sample photo for ${selectedSet.name}`,
        'Sample set photo',
        { enhance: enhancePhoto }
      );
      flashSuccess('Sample photo added successfully!');
      await fetchSetMedia(selectedSet.set_id);
      if (onSetUpdated) onSetUpdated();
    } catch (err: any) {
      console.error('Error uploading sample photo:', err);
      setUploadError(
        `Failed to upload sample photo: ${toErrorMessage(err, 'Unknown error')}`
      );
    } finally {
      setUploading(false);
    }
  };

  const handleUploadPhoto = async () => {
    if (!selectedFile || !selectedSet) return;

    setUploading(true);
    setUploadError(null);
    setUploadSuccess(null);
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    const needsShrink = selectedFile.size > UPLOAD_PROXY_SAFE_MAX_BYTES;
    if (needsShrink) {
      setUploadSuccess(
        `Shrinking large photo (${(selectedFile.size / (1024 * 1024)).toFixed(1)}MB) before upload…`
      );
    }

    try {
      const uploadResponse = await mediaApi.upload(
        selectedFile,
        selectedSet.set_id,
        undefined, // partId
        'photo', // mediaCategory
        `Photo for ${selectedSet.name}`, // description
        'Set photo', // altText
        { enhance: enhancePhoto }
      );

      console.log('Upload successful:', uploadResponse.data);
      const enhanced = Boolean(uploadResponse.data?.processing?.enhance_applied);
      flashSuccess(
        enhanced
          ? needsShrink
            ? 'Photo shrunk, enhanced, and uploaded successfully!'
            : 'Photo enhanced and uploaded successfully!'
          : needsShrink
            ? 'Photo shrunk and uploaded successfully!'
            : 'Photo uploaded successfully!'
      );
      setSelectedFile(null);

      // Refresh the media list
      await fetchSetMedia(selectedSet.set_id);

      // Notify parent component to refresh set data
      if (onSetUpdated) {
        onSetUpdated();
      }
    } catch (err: any) {
      console.error('Error uploading photo:', err);
      setUploadSuccess(null);
      // Do not silently replace a failed upload with the sample photo — that made
      // customer shop show the React/sample image while the admin camera count stayed 0/wrong.
      const detail = toErrorMessage(err, 'upload error', { file: selectedFile });
      const hint =
        typeof err?.response?.data?.hint === 'string'
          ? ` (${err.response.data.hint})`
          : '';
      const actionable =
        /does not accept API|API is not available on this host|file too large|client_max_body_size|shrink|compress/i.test(
          detail
        );
      setUploadError(
        actionable
          ? `Failed to upload photo: ${detail}`
          : `Failed to upload photo: ${detail}${hint}. You can retry, or use “Use sample photo” explicitly.`
      );
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (mediaId: number) => {
    if (window.confirm('Are you sure you want to delete this photo?')) {
      try {
        await mediaApi.delete(mediaId);
        flashSuccess('Photo deleted successfully!');

        if (selectedSet) {
          await fetchSetMedia(selectedSet.set_id);

          // Notify parent component to refresh set data
          if (onSetUpdated) {
            onSetUpdated();
          }
        }
      } catch (err: any) {
        console.error('Error deleting photo:', err);
        setUploadError(`Failed to delete photo: ${toErrorMessage(err, 'Unknown error')}`);
      }
    }
  };

  const handleEditPhoto = async (mediaId: number) => {
    const media = setMedia.find(m => m.media_id === mediaId);
    if (!media) return;

    const newDescription = window.prompt('Edit photo description:', media.description || '');
    if (newDescription === null) return;

    const newAltText = window.prompt('Edit alt text:', media.alt_text || '');
    if (newAltText === null) return;

    try {
      await mediaApi.updateTranslations(mediaId, {
        description: newDescription,
        alt_text: newAltText
      });

      flashSuccess('Photo updated successfully!');

      if (selectedSet) {
        await fetchSetMedia(selectedSet.set_id);
        if (onSetUpdated) onSetUpdated();
      }
    } catch (err: any) {
      console.error('Error updating photo:', err);
      setUploadError(`Failed to update photo: ${toErrorMessage(err, 'Unknown error')}`);
    }
  };

  const handleToggleMasterPhoto = async (mediaId: number, isFeatured: boolean) => {
    try {
      // Optimistic UI so the star updates even if list reorder is subtle
      setSetMedia((prev) =>
        prev.map((m) => ({
          ...m,
          is_featured: m.media_id === mediaId ? isFeatured : isFeatured ? false : m.is_featured,
        }))
      );
      await mediaApi.setFeatured(mediaId, isFeatured);
      flashSuccess(
        isFeatured ? 'Photo marked as master photo!' : 'Master photo status removed!'
      );

      if (selectedSet) {
        await fetchSetMedia(selectedSet.set_id);
        if (onSetUpdated) onSetUpdated();
      }
    } catch (err: any) {
      console.error('Error updating master photo status:', err);
      if (selectedSet) await fetchSetMedia(selectedSet.set_id);
      setUploadError(`Failed to update master photo: ${toErrorMessage(err, 'Unknown error')}`);
    }
  };

  const handleClose = () => {
    onClose();
    setSetMedia([]);
    setSelectedFile(null);
    setCropSourceFile(null);
    setReframeMediaId(null);
    setCropDialogOpen(false);
    setUploadError(null);
    setUploadSuccess(null);
  };

  const wrongHost = typeof window !== 'undefined' && isStaticOnlyLoginHost();

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Manage Photos for {selectedSet?.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ my: 2 }}>
          {wrongHost && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Photo upload does not work on this address ({window.location.host}). Open{' '}
              <a href={`${WORKING_LOGIN_ORIGIN}/sets`}>{WORKING_LOGIN_ORIGIN}</a> — www blocks
              POST /api (nginx 405).
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Recommended photo
            </Typography>
            <Typography variant="body2" component="div">
              {getSetPhotoUploadGuidance()}
            </Typography>
            <Box
              component="ul"
              sx={{ m: 0, mt: 1, pl: 2.5, typography: 'body2', color: 'text.secondary' }}
            >
              <li>Best look: clear product shot, even lighting, little background clutter</li>
              <li>Dimensions: about 1600×1200 (landscape) or 1600×1600 (square)</li>
              <li>Format: JPG (preferred) or PNG — avoid HEIC if the upload fails</li>
              <li>File size: under ~2MB ideal; bigger files are auto-shrunk before upload</li>
              <li>
                After you pick a file, drag the selection area to frame the set (zoom / rotate /
                crop). For photos already in the gallery, use the crop icon to reframe them.
              </li>
              <li>Keep Auto-enhance on for clearer color/contrast when saving</li>
            </Box>
          </Alert>
          <input
            accept="image/jpeg,image/png,image/webp,image/*"
            style={{ display: 'none' }}
            id="photo-upload-input"
            type="file"
            onChange={handleFileChange}
          />
          <label htmlFor="photo-upload-input">
            <Button
              variant="outlined"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
              sx={{ mb: 2 }}
            >
              {uploading ? 'Uploading…' : 'Upload Photo'}
            </Button>
          </label>
          <Button
            variant="outlined"
            onClick={handleUseSamplePhoto}
            disabled={uploading}
            sx={{ mb: 2, ml: 1 }}
          >
            Use sample photo
          </Button>

          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={enhancePhoto}
                  onChange={(e) => setEnhancePhoto(e.target.checked)}
                  disabled={uploading}
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <AutoFixHighIcon fontSize="small" color="primary" />
                  <Box>
                    <Typography variant="body2" component="span">
                      Auto-enhance photo
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Improves color, contrast, and sharpness on the server (no extra AI key needed)
                    </Typography>
                  </Box>
                </Box>
              }
            />
          </Box>

          {selectedFile && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Ready to upload: {selectedFile.name}
                {selectedFile.size > UPLOAD_PROXY_SAFE_MAX_BYTES
                  ? ` (${(selectedFile.size / (1024 * 1024)).toFixed(1)}MB — will be shrunk automatically)`
                  : ''}
              </Typography>
              {previewUrl && (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Cropped preview"
                  sx={{
                    display: 'block',
                    maxWidth: 320,
                    maxHeight: 220,
                    width: '100%',
                    objectFit: 'contain',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    mb: 1.5,
                    bgcolor: 'grey.50',
                  }}
                />
              )}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleUploadPhoto}
                  disabled={uploading}
                  startIcon={uploading ? <CircularProgress size={20} /> : undefined}
                >
                  {uploading
                    ? selectedFile.size > UPLOAD_PROXY_SAFE_MAX_BYTES
                      ? 'Shrinking & uploading…'
                      : 'Uploading…'
                    : 'Upload Photo'}
                </Button>
                <Button variant="outlined" onClick={handleRecrop} disabled={uploading}>
                  Adjust framing
                </Button>
                <Button
                  variant="text"
                  onClick={() => setSelectedFile(null)}
                  disabled={uploading}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          )}

          {uploadSuccess && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setUploadSuccess(null)}>
              {uploadSuccess}
            </Alert>
          )}

          {uploadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
              {uploadError}
            </Alert>
          )}

          {setMedia.length > 0 ? (
            <ImageList cols={3} gap={16}>
              {setMedia.map((media, index) => (
                <ImageListItem key={media.media_id}>
                  <Box sx={{ position: 'relative' }}>
                    {media.file_type === 'image' || media.mime_type?.startsWith('image/') ? (
                      <img
                        src={mediaUrl(media.file_url) || media.file_url}
                        alt={media.alt_text || media.file_name}
                        style={{
                          width: '100%',
                          height: '250px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                          backgroundColor: '#f5f5f5'
                        }}
                        onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                          console.error('Image error:', media.file_url);
                          const img = e.currentTarget;
                          img.style.display = 'none';
                          
                          // Create a more robust fallback
                          const container = img.parentNode as HTMLElement;
                          if (container && !container.querySelector('.image-fallback')) {
                            const fallback = document.createElement('div');
                            fallback.className = 'image-fallback';
                            fallback.style.cssText = `
                              width: 100%;
                              height: 250px;
                              background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
                              border: 2px dashed #ccc;
                              border-radius: 8px;
                              display: flex;
                              flex-direction: column;
                              align-items: center;
                              justify-content: center;
                              color: #666;
                              font-size: 14px;
                              text-align: center;
                              padding: 20px;
                              box-sizing: border-box;
                            `;
                            fallback.innerHTML = `
                              <div style="font-size: 24px; margin-bottom: 8px;">📷</div>
                              <div style="font-size: 14px; margin-bottom: 4px;">Image failed to load</div>
                              <div style="font-size: 12px; margin-top: 4px; color: #888;">${media.file_name || ''}</div>
                              <div style="font-size: 10px; margin-top: 8px; color: #666; max-width: 90%; word-break: break-all;">URL: ${mediaUrl(media.file_url) || media.file_url || ''}</div>
                              <div style="font-size: 10px; margin-top: 6px; color: #888; text-align: center; max-width: 90%;">Photo missing on API disk, or /api/uploads is not reachable. Re-upload or check UPLOADS_DIR on HSL.</div>
                            `;
                            container.appendChild(fallback);
                          }
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', media.file_url);
                        }}
                      />
                    ) : (
                      // Document/PDF display
                      <Box sx={{ 
                        position: 'relative',
                        width: '100%',
                        height: '250px',
                        border: '2px dashed #ccc',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#e8f4fd',
                          borderColor: '#1976d2'
                        }
                      }}
                      onClick={() => window.open(mediaUrl(media.file_url) || media.file_url, '_blank')}
                    >
                      <Box sx={{ fontSize: '48px', marginBottom: '16px', opacity: 0.7 }}>
                        {media.mime_type === 'application/pdf' ? '📄' : '📁'}
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: '8px', textAlign: 'center' }}>
                        {media.file_name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                        {media.media_category || 'Document'}
                      </Typography>
                    </Box>
                    )}
                    
                    <ImageListItemBar
                      title={media.description || media.file_name}
                      subtitle={media.alt_text}
                      actionIcon={
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            sx={{ 
                              backgroundColor: isFeaturedMedia(media) ? 'rgba(255, 215, 0, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                              color: isFeaturedMedia(media) ? 'white' : 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                backgroundColor: isFeaturedMedia(media) ? 'rgba(255, 100, 100, 0.9)' : 'rgba(255, 215, 0, 0.9)',
                                color: 'white'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleMasterPhoto(media.media_id, !isFeaturedMedia(media));
                            }}
                            aria-label={isFeaturedMedia(media) ? `Remove master photo status` : `Mark as master photo`}
                          >
                            {isFeaturedMedia(media) ? <StarIcon /> : <StarBorderIcon />}
                          </IconButton>
                          <IconButton
                            sx={{
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                backgroundColor: 'rgba(25, 118, 210, 0.95)',
                                color: 'white',
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReframePhoto(media.media_id);
                            }}
                            disabled={uploading}
                            aria-label="Crop, zoom, or rotate photo"
                            title="Crop / zoom / rotate"
                          >
                            <CropIcon />
                          </IconButton>
                          <IconButton
                            sx={{ 
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 100, 100, 0.9)',
                                color: 'white'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditPhoto(media.media_id);
                            }}
                            aria-label="Edit photo description"
                            title="Edit description / alt text"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            sx={{ 
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 100, 100, 0.9)',
                                color: 'white'
                              }
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePhoto(media.media_id);
                            }}
                            aria-label="Delete photo"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      }
                    />
                  </Box>
                </ImageListItem>
              ))}
            </ImageList>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Box
                component="img"
                src={getSampleSetPhotoUrl(selectedSet?.category)}
                alt="Sample set photo placeholder"
                sx={{
                  width: 220,
                  height: 160,
                  objectFit: 'cover',
                  borderRadius: 2,
                  mb: 2,
                  border: '1px dashed',
                  borderColor: 'divider',
                }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                No photos for this set yet — the sample image is shown in the shop until you add one.
              </Typography>
              <Button
                variant="contained"
                onClick={handleUseSamplePhoto}
                disabled={uploading}
                size="small"
              >
                Attach sample photo to this set
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>

      <PhotoCropDialog
        open={cropDialogOpen}
        file={cropSourceFile}
        onCancel={handleCropCancel}
        onConfirm={handleCropConfirm}
        onSkip={reframeMediaId == null ? handleCropSkip : undefined}
        showSkip={reframeMediaId == null}
        title={
          reframeMediaId != null ? 'Reframe this photo' : 'Frame your photo'
        }
        confirmLabel={
          reframeMediaId != null ? 'Save framing' : 'Use this framing'
        }
      />
    </Dialog>
  );
};

export default SetPhotoDialog;
